import React, { useRef, useEffect } from 'react';
import { ChatBubble, Avatar } from '@cloudscape-design/chat-components';
import { SpaceBetween, Box, Input, Button, Spinner } from '@cloudscape-design/components';
import { AIChatDrawerProps } from '../../types';

/**
 * Simple honest loading indicator — just a spinner, no fake status messages.
 */
const ThinkingIndicator: React.FC = () => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
    <Spinner size="normal" />
    <span style={{ fontWeight: 500, color: 'var(--color-text-body-secondary, #5f6b7a)' }}>
      Thinking…
    </span>
  </div>
);

/**
 * Simple markdown-to-JSX renderer for chat messages.
 * Handles: **bold**, *italic*, `inline code`, ```code blocks```,
 * - bullet lists, numbered lists, and line breaks.
 */
const renderMarkdown = (text: string): React.ReactNode => {
  if (!text) return null;

  // Split by code blocks first (```...```)
  const codeBlockRegex = /```(\w*)\n?([\s\S]*?)```/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  while ((match = codeBlockRegex.exec(text)) !== null) {
    // Render text before the code block
    if (match.index > lastIndex) {
      parts.push(...renderInlineMarkdown(text.slice(lastIndex, match.index), parts.length));
    }
    // Render code block
    parts.push(
      <pre
        key={`code-${parts.length}`}
        style={{
          background: 'var(--color-background-container-header, #f2f3f3)',
          padding: '8px 12px',
          borderRadius: '6px',
          overflowX: 'auto',
          fontSize: '13px',
          margin: '4px 0',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        <code>{match[2].trim()}</code>
      </pre>,
    );
    lastIndex = match.index + match[0].length;
  }

  // Render remaining text after last code block
  if (lastIndex < text.length) {
    parts.push(...renderInlineMarkdown(text.slice(lastIndex), parts.length));
  }

  return <>{parts}</>;
};

/**
 * Render inline markdown (everything except code blocks).
 * Splits by line, handles headers, tables, lists, bold, italic, inline code.
 */
const renderInlineMarkdown = (text: string, keyOffset: number): React.ReactNode[] => {
  const lines = text.split('\n');
  const result: React.ReactNode[] = [];
  let listItems: React.ReactNode[] = [];
  let inList = false;
  let listType: 'ul' | 'ol' = 'ul';
  let tableRows: string[][] = [];
  let tableHeader: string[] = [];
  let inTable = false;

  const flushList = () => {
    if (listItems.length > 0) {
      if (listType === 'ul') {
        result.push(
          <ul
            key={`list-${keyOffset}-${result.length}`}
            style={{ margin: '4px 0', paddingLeft: '20px' }}
          >
            {listItems}
          </ul>,
        );
      } else {
        result.push(
          <ol
            key={`list-${keyOffset}-${result.length}`}
            style={{ margin: '4px 0', paddingLeft: '20px' }}
          >
            {listItems}
          </ol>,
        );
      }
      listItems = [];
      inList = false;
    }
  };

  const flushTable = () => {
    if (tableHeader.length > 0 || tableRows.length > 0) {
      const tableStyle: React.CSSProperties = {
        borderCollapse: 'collapse',
        width: '100%',
        fontSize: '13px',
        margin: '8px 0',
      };
      const thStyle: React.CSSProperties = {
        border: '1px solid var(--color-border-divider-default, #d1d5db)',
        padding: '6px 10px',
        textAlign: 'left',
        fontWeight: 'bold',
        background: 'var(--color-background-container-header, #f2f3f3)',
      };
      const tdStyle: React.CSSProperties = {
        border: '1px solid var(--color-border-divider-default, #d1d5db)',
        padding: '6px 10px',
        textAlign: 'left',
      };

      result.push(
        <div key={`table-${keyOffset}-${result.length}`} style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            {tableHeader.length > 0 && (
              <thead>
                <tr>
                  {tableHeader.map((cell, ci) => (
                    <th key={`th-${ci}`} style={thStyle}>
                      {renderInlineFormatting(cell.trim())}
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {tableRows.map((row, ri) => (
                <tr key={`tr-${ri}`}>
                  {row.map((cell, ci) => (
                    <td key={`td-${ri}-${ci}`} style={tdStyle}>
                      {renderInlineFormatting(cell.trim())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      tableHeader = [];
      tableRows = [];
      inTable = false;
    }
  };

  const parseTableRow = (line: string): string[] => {
    // Remove leading/trailing pipes and split by pipe
    const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '');
    return trimmed.split('|');
  };

  const isTableSeparator = (line: string): boolean => {
    // Matches lines like |---|---|---| or |:---:|:---| etc.
    return /^\|?\s*[-:]+[-|\s:]*$/.test(line.trim());
  };

  lines.forEach((line, lineIdx) => {
    const trimmedLine = line.trim();

    // Table rows: | cell | cell | cell |
    if (trimmedLine.includes('|') && trimmedLine.match(/\|.*\|/)) {
      // Skip separator rows (|---|---|)
      if (isTableSeparator(trimmedLine)) {
        return;
      }

      flushList();

      const cells = parseTableRow(trimmedLine);
      if (!inTable) {
        // First row is the header
        inTable = true;
        tableHeader = cells;
      } else {
        tableRows.push(cells);
      }
      return;
    }

    // If we were in a table and hit a non-table line, flush
    if (inTable) {
      flushTable();
    }

    // Headers: # ## ### #### etc.
    const headerMatch = trimmedLine.match(/^(#{1,6})\s+(.+)$/);
    if (headerMatch) {
      flushList();
      const level = headerMatch[1].length;
      const headerText = headerMatch[2];
      const fontSize = level <= 2 ? '1.1em' : level === 3 ? '1em' : '0.95em';
      const marginTop = level <= 2 ? '12px' : '8px';
      result.push(
        <div
          key={`h-${keyOffset}-${lineIdx}`}
          style={{
            fontSize,
            fontWeight: 'bold',
            marginTop,
            marginBottom: '4px',
          }}
        >
          {renderInlineFormatting(headerText)}
        </div>,
      );
      return;
    }

    // Bullet list items: - item or * item
    const bulletMatch = trimmedLine.match(/^[-*]\s+(.+)$/);
    if (bulletMatch) {
      if (!inList || listType !== 'ul') {
        flushList();
        inList = true;
        listType = 'ul';
      }
      listItems.push(
        <li key={`li-${keyOffset}-${lineIdx}`}>{renderInlineFormatting(bulletMatch[1])}</li>,
      );
      return;
    }

    // Numbered list items: 1. item
    const numberedMatch = trimmedLine.match(/^\d+\.\s+(.+)$/);
    if (numberedMatch) {
      if (!inList || listType !== 'ol') {
        flushList();
        inList = true;
        listType = 'ol';
      }
      listItems.push(
        <li key={`li-${keyOffset}-${lineIdx}`}>{renderInlineFormatting(numberedMatch[1])}</li>,
      );
      return;
    }

    // Non-list line — flush any pending list
    flushList();

    // Empty line = paragraph break
    if (trimmedLine === '') {
      result.push(<br key={`br-${keyOffset}-${lineIdx}`} />);
      return;
    }

    // Regular text line
    result.push(
      <span key={`line-${keyOffset}-${lineIdx}`}>
        {renderInlineFormatting(trimmedLine)}
        {lineIdx < lines.length - 1 ? <br /> : null}
      </span>,
    );
  });

  // Flush remaining lists and tables
  flushList();
  flushTable();

  return result;
};

/**
 * Render inline formatting: **bold**, *italic*, `code`
 */
const renderInlineFormatting = (text: string): React.ReactNode => {
  // Process inline formatting using regex
  const parts: React.ReactNode[] = [];
  // Pattern matches: **bold**, *italic*, `code`
  const inlineRegex = /(\*\*(.+?)\*\*|\*(.+?)\*|`([^`]+)`)/g;
  let lastIdx = 0;
  let inlineMatch;

  while ((inlineMatch = inlineRegex.exec(text)) !== null) {
    // Text before match
    if (inlineMatch.index > lastIdx) {
      parts.push(text.slice(lastIdx, inlineMatch.index));
    }

    if (inlineMatch[2]) {
      // **bold**
      parts.push(<strong key={`b-${inlineMatch.index}`}>{inlineMatch[2]}</strong>);
    } else if (inlineMatch[3]) {
      // *italic*
      parts.push(<em key={`i-${inlineMatch.index}`}>{inlineMatch[3]}</em>);
    } else if (inlineMatch[4]) {
      // `code`
      parts.push(
        <code
          key={`c-${inlineMatch.index}`}
          style={{
            background: 'var(--color-background-container-header, #f2f3f3)',
            padding: '1px 4px',
            borderRadius: '3px',
            fontSize: '0.9em',
          }}
        >
          {inlineMatch[4]}
        </code>,
      );
    }

    lastIdx = inlineMatch.index + inlineMatch[0].length;
  }

  // Remaining text after last match
  if (lastIdx < text.length) {
    parts.push(text.slice(lastIdx));
  }

  return parts.length === 1 ? parts[0] : <>{parts}</>;
};

const AIChatDrawer: React.FC<AIChatDrawerProps> = ({
  messages,
  chatInput,
  onChatInputChange,
  onSendMessage,
  isLoading,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  return (
    <SpaceBetween direction="vertical" size="m">
      <Box padding="s">
        <SpaceBetween direction="vertical" size="s">
          <Box fontSize="heading-m" fontWeight="bold">
            Catalyst AI Assistant
          </Box>
          <Box margin={{ bottom: 's' }} padding="s">
            <SpaceBetween direction="vertical" size="s">
              {/* Welcome message if no conversation yet */}
              {messages.length === 0 && (
                <ChatBubble
                  avatar={<Avatar color="gen-ai" iconName="gen-ai" ariaLabel="AI Assistant" />}
                  type="incoming"
                  ariaLabel="AI Assistant welcome message"
                >
                  {renderMarkdown(
                    `Hello! I'm Catalyst AI, your pipeline assistant. I can help you with:\n\n` +
                      `- 📊 **Pipeline metrics** — "How many records did bid-item produce in the last hour?"\n` +
                      `- 🔍 **Code explanations** — "How is _received_date calculated in the quote table?"\n` +
                      `- ⚙️ **Pipeline configs** — "Show me the components of the container-move pipeline"\n` +
                      `- 📋 **List pipelines** — "What pipelines are available?"\n\n` +
                      `Ask me anything about your data pipelines!`,
                  )}
                </ChatBubble>
              )}

              {/* Render conversation messages */}
              {messages.map((msg) => (
                <ChatBubble
                  key={msg.id}
                  avatar={
                    msg.role === 'assistant' ? (
                      <Avatar color="gen-ai" iconName="gen-ai" ariaLabel="AI Assistant" />
                    ) : (
                      <Avatar iconName="user-profile" ariaLabel="User" />
                    )
                  }
                  type={msg.role === 'assistant' ? 'incoming' : 'outgoing'}
                  ariaLabel={`${msg.role === 'assistant' ? 'AI Assistant' : 'User'} message`}
                >
                  {msg.loading ? (
                    <ThinkingIndicator />
                  ) : msg.role === 'assistant' ? (
                    <>
                      {renderMarkdown(msg.content)}
                      {msg.isTyping && (
                        <span
                          style={{
                            display: 'inline-block',
                            width: '2px',
                            height: '1em',
                            background: 'var(--color-text-interactive-default, #0972d3)',
                            marginLeft: '1px',
                            verticalAlign: 'text-bottom',
                            animation: 'cursor-blink 1s step-end infinite',
                          }}
                        />
                      )}
                      <style>
                        {`@keyframes cursor-blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }`}
                      </style>
                    </>
                  ) : (
                    msg.content
                  )}
                </ChatBubble>
              ))}
              <div ref={messagesEndRef} />
            </SpaceBetween>
          </Box>
        </SpaceBetween>
      </Box>
      <Box padding={{ horizontal: 's' }}>
        <SpaceBetween direction="horizontal" size="xs">
          <div style={{ flex: 1 }}>
            <Input
              placeholder="Ask about your pipelines..."
              value={chatInput}
              onChange={({ detail }) => onChatInputChange(detail.value)}
              onKeyDown={({ detail }) => {
                if (detail.key === 'Enter' && !isLoading) {
                  onSendMessage();
                }
              }}
              disabled={isLoading}
            />
          </div>
          <Button
            iconName="send"
            variant="primary"
            onClick={onSendMessage}
            disabled={isLoading || !chatInput.trim()}
            loading={isLoading}
          >
            Send
          </Button>
        </SpaceBetween>
      </Box>
    </SpaceBetween>
  );
};

export default AIChatDrawer;
